import { AnimatePresence, motion } from 'framer-motion';
import { type Translate } from 'next-translate';
import React, {
	type Dispatch,
	type RefObject,
	type SetStateAction,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useIntersectionObserver, useIsTablet } from '@/hooks';
import { AmplitudeService } from '@/services';
import { removeUrlParam } from '@/helpers';
import { LandingTool, type LandingTools } from '@/data/aiToolLandings';
import { AMPLITUDE_EVENTS } from '@/constants';
import { searchVariants } from '@/animations';
import { SearchDesktop } from '../Search/SearchDesktop';
import { SearchMobile } from '../Search/SearchMobile';
import { SearchTab } from '../Search/SearchTab';
import styles from './ToolsTabs.module.scss';

interface TToolsTabsProps {
	t: Translate;
	data: LandingTools['hero'];
	activeTabId: string;
	setActiveTabId: Dispatch<SetStateAction<string>>;
	isTabsSticky: boolean;
	setTabsSticky: Dispatch<SetStateAction<boolean>>;
	hoveredTabId: string | null;
	setHoveredTabId: Dispatch<SetStateAction<string | null>>;
	activePositioningId: string | null;
	setActivePositioningId: Dispatch<SetStateAction<string | null>>;
	setActiveFeature: Dispatch<SetStateAction<LandingTool | null>>;
	setPinnedFeatureId: Dispatch<SetStateAction<string | null>>;
	scrollToolsIntoViewIfNeeded: () => void;
	toolsContainerRef: RefObject<HTMLDivElement>;
}

const HOVER_DELAY = 300;

export const ToolsTabs = ({
	t,
	data,
	activeTabId,
	setActiveTabId,
	isTabsSticky,
	setTabsSticky,
	hoveredTabId,
	setHoveredTabId,
	activePositioningId,
	setActivePositioningId,
	setActiveFeature,
	setPinnedFeatureId,
	scrollToolsIntoViewIfNeeded,
	toolsContainerRef,
}: TToolsTabsProps) => {
	const isTablet = useIsTablet();
	const topMarkerRef = useRef<HTMLDivElement>(null);
	const tabsRef = useRef<HTMLDivElement>(null);
	const tabsScrollContainerRef = useRef<HTMLDivElement>(null);

	const [withBackground, setWithBackground] = useState(false);
	const [searchActive, setSearchActive] = useState(false);

	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

	const startHoverTimer = (id: string | null) => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
		}

		hoverTimerRef.current = setTimeout(() => {
			handleHover(id);
		}, HOVER_DELAY);
	};

	const clearHoverTimer = () => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	};

	useIntersectionObserver(topMarkerRef, setTabsSticky);
	useIntersectionObserver(topMarkerRef, setWithBackground, '-64px 0px 0px 0px');

	const handleHover = (tabId: string | null) => {
		setHoveredTabId(tabId);
		if (tabId && tabId !== activeTabId) {
			setActiveFeature(null);
			setActivePositioningId(null);
			setPinnedFeatureId(null);
		}
	};

	const handleTabClick = (
		tabId: string,
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		setActiveTabId(tabId);
		setActiveFeature(null);
		setActivePositioningId(null);
		setPinnedFeatureId(null);

		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_CATEGORY_SELECTED, {
			type: tabId,
		});
		removeUrlParam('category');
		removeUrlParam('feature');
		clearHoverTimer();
		scrollToolsIntoViewIfNeeded();

		if (isTablet) {
			scrollTabIntoView(event.currentTarget);
		}
	};

	const handleMouseEnter = (tabId: string, isTabActive: boolean) => {
		if (isTabActive) {
			clearHoverTimer();
			handleHover(null);
			return;
		}
		startHoverTimer(tabId);
	};

	const handleMouseLeave = (isTabActive: boolean) => {
		if (isTabActive) {
			clearHoverTimer();
			return;
		}
		startHoverTimer(null);
	};

	const scrollTabIntoView = (element: HTMLButtonElement) => {
		const scrollContainer = tabsScrollContainerRef.current;

		if (scrollContainer) {
			element.scrollIntoView({
				behavior: 'smooth',
				inline: 'center',
				block: 'nearest',
			});
		}
	};

	useEffect(() => {
		if (!isTablet) return;

		const scrollContainer = tabsScrollContainerRef.current;
		if (!scrollContainer) return;

		const button = scrollContainer.querySelector(
			`button[data-tab-id='${activeTabId}']`,
		);
		const activeTabButton = button as HTMLButtonElement | null;

		if (activeTabButton) {
			setTimeout(() => scrollTabIntoView(activeTabButton), 250);
		}
	}, [activeTabId, isTablet, activePositioningId]);

	return (
		<>
			<div ref={topMarkerRef} className={styles['top-marker']} />

			{isTabsSticky && tabsRef.current && (
				<motion.div
					key='sticky-stub'
					initial={{ height: tabsRef.current.clientHeight }}
					exit={{ height: 0 }}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
				/>
			)}

			<div
				ref={tabsRef}
				className={`
					${styles['tabs']}
					${isTabsSticky ? styles['tabs_sticky'] : ''}
					${withBackground ? styles['tabs_background'] : ''}
				`}
			>
				<div className={styles['tabs__container']} ref={tabsScrollContainerRef}>
					<AnimatePresence mode='wait' initial={false}>
						{/* DESKTOP SEARCH MODE */}
						{searchActive && !isTablet ? (
							<SearchDesktop
								data={data}
								setActiveTabId={setActiveTabId}
								setSearchActive={setSearchActive}
								setActiveFeature={setActiveFeature}
								setActivePositioningId={setActivePositioningId}
								setPinnedFeatureId={setPinnedFeatureId}
							/>
						) : (
							<motion.div
								key='tabs'
								variants={searchVariants}
								initial='initial'
								animate='animate'
								exit='exit'
								transition={{ duration: 0.12, ease: 'easeOut' }}
								style={{ display: 'contents' }}
							>
								{data.tabs.map(tab => {
									if (tab.id === 'search') {
										return (
											<SearchTab
												key={tab.id}
												tabData={tab}
												setSearchActive={setSearchActive}
											/>
										);
									}

									const isTabActive = activeTabId === tab.id;
									const isTabHovered = hoveredTabId === tab.id;

									return (
										<button
											key={tab.id}
											data-tab-id={tab.id}
											type='button'
											className={`
												${styles['tabs__item']}
												${isTabActive ? styles['tabs__item_active'] : ''}
												${isTabHovered ? styles['tabs__item_hover'] : ''}
											`}
											onClick={event => handleTabClick(tab.id, event)}
											onMouseEnter={() => handleMouseEnter(tab.id, isTabActive)}
											onMouseLeave={() => handleMouseLeave(isTabActive)}
										>
											{tab.emoji && (
												<span className={styles['tabs__item_emoji']}>
													{tab.emoji}
												</span>
											)}

											<span className={styles['tabs__item_title']}>
												{t(tab.title)}
											</span>
										</button>
									);
								})}
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>

			{/* MOBILE / TABLET SEARCH MODE */}
			{searchActive && isTablet && (
				<SearchMobile
					key='mobile-search'
					data={data}
					searchActive={searchActive}
					setActiveTabId={setActiveTabId}
					setSearchActive={setSearchActive}
					setActiveFeature={setActiveFeature}
					setActivePositioningId={setActivePositioningId}
					setPinnedFeatureId={setPinnedFeatureId}
					toolsContainer={toolsContainerRef}
				/>
			)}
		</>
	);
};
